// ─────────────────────────────────────────────────────────────────────────────
// DispatcherDashboard.jsx  —  Root orchestrator component
//
// This file owns ALL state and contains ONLY layout + wiring logic.
// Nothing visual to change here — edit sub-components instead.
//
// FILE MAP — where to go when you need to change something:
// ┌─────────────────────────────────────────────────────────────────┐
// │  constants.js           ← mock data, nav items, summary cards  │
// │  Sidebar.jsx            ← left nav drawer (logo, nav, CTA)     │
// │  TopBar.jsx             ← search bar, bell, user avatar        │
// │  BottomBar.jsx          ← mobile bottom tab bar                │
// │  TrackingMap.jsx        ← SVG GPS map with animated truck      │
// │  RightPanel.jsx         ← Detail / Chat / Alerts / Messages    │
// │  Modals.jsx             ← GPS Recall + Map Popup modals        │
// │  DispatcherDashboard.jsx← THIS FILE — state + layout only     │
// └─────────────────────────────────────────────────────────────────┘
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import Sidebar     from "./Includes/Sidebar";
import TopBar      from "./Includes/TopBar";
import BottomBar   from "./Includes/BottomBar";
import TrackingMap from "./Includes/TrackingMap";
import RightPanel  from "./Includes/RightPanel";
import { GPSModal, MapPopupModal } from "./Includes/Modals";
import { LOADS, DRIVERS, GPS_ROUTE, SUMMARY_CARDS, STEPS_TOTAL } from "./Includes/constants";

// ── Order Row (local to this file — used in both desktop + mobile) ────────────
function StatusPill({ status }) {
  const map = {"In Progress":"bg-blue-100 text-blue-700", Pending:"bg-orange-100 text-orange-600", Completed:"bg-green-100 text-green-700", Delayed:"bg-red-100 text-red-600"};
  return <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${map[status]||"bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function OrderRow({ load, selected, onClick }) {
  const iconBg    = load.status==="In Progress"?"bg-blue-100":load.status==="Pending"?"bg-orange-100":load.status==="Completed"?"bg-green-100":"bg-red-100";
  const iconColor = load.status==="In Progress"?"text-blue-600":load.status==="Pending"?"text-orange-500":load.status==="Completed"?"text-green-600":"text-red-500";
  return (
    <div onClick={onClick} className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors ${selected?"bg-blue-50 border-l-2 border-l-blue-600":"hover:bg-gray-50"}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${iconBg}`}>
        <svg className={`w-5 h-5 ${iconColor}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 17H5a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3m-1 11a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2h8z"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{load.id}</span>
          <StatusPill status={load.status}/>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{load.origin} → {load.destination}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">Driver: {load.driver}</span>
          {load.eta !== "—" && (
            <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
              ETA {load.eta}<span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block"/>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Summary Card (local reusable) ─────────────────────────────────────────────
function SummaryCard({ c, compact = false }) {
  const bg = c.color==="blue"?"bg-blue-50":c.color==="green"?"bg-green-50":c.color==="red"?"bg-red-50":"bg-purple-50";
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 lg:p-4">
      <div className="flex items-start justify-between mb-1.5 lg:mb-2">
        <div className={`${compact?"w-9 h-9":"w-10 h-10"} rounded-xl flex items-center justify-center text-base lg:text-lg ${bg}`}>{c.icon}</div>
        {c.delta && <span className={`text-xs font-semibold ${c.up?"text-green-600":"text-red-500"}`}>{c.delta}</span>}
      </div>
      <p className={`text-gray-400 font-medium ${compact?"text-[10px]":"text-xs"}`}>{c.label}</p>
      <p className={`font-bold text-gray-900 mt-0.5 ${compact?"text-xl":"text-2xl"}`}>{c.val}</p>
      {c.sub && <p className={`text-gray-400 ${compact?"text-[10px]":"text-xs mt-0.5"}`}>{c.sub}</p>}
      {c.prog != null && (
        <div className={compact?"mt-1.5":"mt-2"}>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{width:`${c.prog}%`}}/>
          </div>
          <p className={`text-gray-400 text-right ${compact?"text-[10px] mt-0.5":"text-xs mt-1"}`}>{c.prog}%</p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────

export default function DispatcherDashboard() {

  // ── Nav / layout state ────────────────────────────────────────────────────
  const [activeNav,   setActiveNav]   = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileView,  setMobileView]  = useState("dashboard");
  const [rightTab,    setRightTab]    = useState("detail");
  const [notifCount,  setNotifCount]  = useState(3);

  // ── Load / driver selection ───────────────────────────────────────────────
  const [selectedLoad, setSelectedLoad] = useState(LOADS[0]);
  const [orderFilter,  setOrderFilter]  = useState("In Progress");
  const [detailOpen,   setDetailOpen]   = useState(true);

  // ── Modals ────────────────────────────────────────────────────────────────
  const [gpsModal,    setGpsModal]    = useState(false);
  const [mapPopupDrv, setMapPopupDrv] = useState(null);

  // ── GPS simulation state ──────────────────────────────────────────────────
  const [simRunning, setSimRunning] = useState(false);
  const [simIdx,     setSimIdx]     = useState(0);
  const [simProg,    setSimProg]    = useState(0);
  const [simSpeed,   setSimSpeed]   = useState(0);
  const simRef = useRef(null);

  // ── Derived values ────────────────────────────────────────────────────────
  const stepsCompleted = Math.min(simIdx*20 + Math.round(simProg*20), STEPS_TOTAL);
  const pct      = Math.round((stepsCompleted / STEPS_TOTAL) * 100);
  const etaMins  = Math.max(0, Math.round(((STEPS_TOTAL - stepsCompleted) / STEPS_TOTAL) * 320));
  const selectedDriver = selectedLoad ? DRIVERS.find(d => d.id === selectedLoad.driverId) : null;
  const filteredLoads  = orderFilter === "All" ? LOADS : LOADS.filter(l => l.status === orderFilter);

  // ── Simulation ticker ─────────────────────────────────────────────────────
  useEffect(() => {
    if (simRunning) {
      simRef.current = setInterval(() => {
        setSimSpeed(Math.floor(52 + Math.random() * 22));
        setSimProg(prev => {
          if (prev >= 1) {
            setSimIdx(i => { if (i >= GPS_ROUTE.length-2) { setSimRunning(false); return i; } return i+1; });
            return 0;
          }
          return prev + 0.05;
        });
      }, 350);
    } else {
      clearInterval(simRef.current);
      if (!simRunning) setSimSpeed(0);
    }
    return () => clearInterval(simRef.current);
  }, [simRunning]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const resetSim = () => { setSimRunning(false); setSimIdx(0); setSimProg(0); setSimSpeed(0); };

  const handleSelectLoad = (load) => {
    setSelectedLoad(load);
    setDetailOpen(true);
    setMobileView("detail");
  };

  // ── Map controls (reused in desktop + mobile map view) ───────────────────
  const MapControls = ({ compact = false }) => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => setSimRunning(r => !r)}
        className={`text-xs px-2.5 py-1.5 rounded-lg font-semibold border transition-colors ${
          simRunning ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
        }`}
      >
        {simRunning ? (compact ? "⏸" : "⏸ Pause") : (compact ? "▶ Start" : "▶ Start")}
      </button>
      <button onClick={resetSim} className="text-xs px-2.5 py-1.5 rounded-lg font-semibold bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100">
        ↺ {compact ? "" : "Reset"}
      </button>
      {!compact && (
        <button className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1 font-semibold">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
          </svg>
          Filter
        </button>
      )}
      <button onClick={() => setGpsModal(true)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 transition-colors">
        📡 {compact ? "GPS" : "GPS Recall"}
      </button>
    </div>
  );

  // ── Progress bar strip below map ──────────────────────────────────────────
  const MapProgress = ({ compact = false }) => (
    <div className={`${compact ? "px-4 py-2 bg-white border-t" : "px-4 py-2 border-t border-gray-100"} flex-shrink-0`}>
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{compact ? "Bakersfield" : "Bakersfield, CA"}</span>
        <span className="font-semibold text-gray-600">{pct}%</span>
        <span>{compact ? "Phoenix" : "Phoenix, AZ"}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-300" style={{width:`${pct}%`, background:"linear-gradient(90deg,#3b82f6,#f59e0b)"}}/>
      </div>
    </div>
  );

  // ── Sim overlay shown on top of map ──────────────────────────────────────
  const SimOverlay = ({ compact = false }) => simRunning ? (
    <div className={`absolute top-2 left-2 bg-white/95 border border-gray-200 rounded-xl px-3 py-2 shadow${compact?"":"-sm"} text-xs space-y-${compact?"0.5":"1"}`}>
      <div className="text-gray-500">Speed: <span className="font-bold text-gray-800">{simSpeed} km/h</span></div>
      <div className="text-gray-500">ETA: <span className="font-bold text-green-700">{etaMins > 0 ? `${Math.floor(etaMins/60)}h ${etaMins%60}m` : "Arrived"}</span></div>
      {!compact && <div className="text-gray-500">Progress: <span className="font-bold text-blue-700">{pct}%</span></div>}
    </div>
  ) : null;

  // ── Back arrow used in mobile sub-views ───────────────────────────────────
  const BackButton = ({ to }) => (
    <button onClick={() => setMobileView(to)} className="text-blue-600">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
      </svg>
    </button>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{fontFamily:"Inter,system-ui,sans-serif"}}>

      {/* ── SIDEBAR ── */}
      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
      />

      {/* ── MAIN AREA ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* ── TOP BAR ── */}
    

        {/* ══════════════════════════════════════════════════════════════════
            DESKTOP LAYOUT (lg and above)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex-1 overflow-hidden hidden lg:flex">

          {/* Left scrollable column */}
          <div className="flex-1 overflow-y-auto flex flex-col min-w-0">

            {/* 5-column summary cards */}
            <div className="grid grid-cols-5 gap-3 p-4 pb-3 flex-shrink-0">
              {SUMMARY_CARDS.map((c, i) => <SummaryCard key={i} c={c}/>)}
            </div>

            {/* Orders + Map row */}
            <div className="flex gap-3 px-4 pb-4 flex-1 min-h-0">

              {/* Orders panel */}
              <div className="w-72 flex-shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
                <div className="px-4 pt-4 pb-2 flex-shrink-0">
                  <h2 className="font-bold text-gray-900 text-base mb-3">Orders</h2>
                  <div className="flex gap-1 border-b border-gray-100 pb-2">
                    {["All","In Progress","Pending","Completed"].map(f => (
                      <button key={f} onClick={() => setOrderFilter(f)}
                        className={`px-2 py-1 rounded text-xs font-semibold transition-colors whitespace-nowrap ${
                          orderFilter===f ? "text-blue-700 border-b-2 border-blue-600" : "text-gray-400 hover:text-gray-700"
                        }`}
                      >{f}</button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredLoads.map(load => (
                    <OrderRow key={load.id} load={load}
                      selected={selectedLoad?.id === load.id}
                      onClick={() => handleSelectLoad(load)}
                    />
                  ))}
                </div>
                <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-semibold">View all orders →</button>
                </div>
              </div>

              {/* Live Tracking map */}
              <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden min-w-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">Live Tracking</span>
                    {simRunning && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>}
                  </div>
                  <MapControls/>
                </div>
                <div className="flex-1 relative overflow-hidden min-h-0">
                  <TrackingMap simIdx={simIdx} simProg={simProg} simRunning={simRunning} hlDriver={null}/>
                  <SimOverlay/>
                </div>
                <MapProgress/>
              </div>
            </div>
          </div>

          {/* Right panel — fixed 320px */}
          <div className="w-80 flex-shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
            <RightPanel
              rightTab={rightTab} setRightTab={setRightTab}
              selectedLoad={selectedLoad} selectedDriver={selectedDriver}
              detailOpen={detailOpen} setDetailOpen={setDetailOpen}
              simRunning={simRunning} simSpeed={simSpeed}
              openGPSRecall={() => setGpsModal(true)}
            />
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            MOBILE LAYOUT (below lg)
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex-1 overflow-hidden flex flex-col lg:hidden">

          {/* ── Dashboard home ── */}
          {mobileView === "dashboard" && (
            <div className="flex-1 overflow-y-auto pb-16">
              <div className="grid grid-cols-2 gap-2 p-3">
                {SUMMARY_CARDS.map((c, i) => <SummaryCard key={i} c={c} compact/>)}
              </div>

              {/* Orders preview */}
              <div className="bg-white rounded-2xl mx-3 mb-3 shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 pt-4 pb-2">
                  <h2 className="font-bold text-gray-900 text-base mb-2">Orders</h2>
                  <div className="flex gap-1 border-b border-gray-100 pb-2 overflow-x-auto">
                    {["All","In Progress","Pending","Completed"].map(f => (
                      <button key={f} onClick={() => setOrderFilter(f)}
                        className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${orderFilter===f?"text-blue-700 border-b-2 border-blue-600":"text-gray-400"}`}
                      >{f}</button>
                    ))}
                  </div>
                </div>
                {filteredLoads.map(load => (
                  <OrderRow key={load.id} load={load}
                    selected={selectedLoad?.id === load.id}
                    onClick={() => handleSelectLoad(load)}
                  />
                ))}
                <div className="px-4 py-3 border-t">
                  <button onClick={() => setMobileView("orders")} className="text-sm text-blue-600 font-semibold">View all orders →</button>
                </div>
              </div>

              {/* Map preview */}
              <div className="bg-white rounded-2xl mx-3 mb-3 shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <span className="font-bold text-gray-900">Live Tracking</span>
                  <button onClick={() => setMobileView("map")} className="text-xs text-blue-600 font-semibold">Full Map →</button>
                </div>
                <div className="h-48 relative">
                  <TrackingMap simIdx={simIdx} simProg={simProg} simRunning={simRunning} hlDriver={null}/>
                </div>
                <MapProgress compact/>
              </div>
            </div>
          )}

          {/* ── Orders full view ── */}
          {mobileView === "orders" && (
            <div className="flex-1 overflow-hidden flex flex-col pb-16">
              <div className="bg-white border-b px-4 py-3 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                  <BackButton to="dashboard"/>
                  <h2 className="font-bold text-gray-900">Orders</h2>
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {["All","In Progress","Pending","Completed"].map(f => (
                    <button key={f} onClick={() => setOrderFilter(f)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${orderFilter===f?"bg-blue-600 text-white":"bg-gray-100 text-gray-500"}`}
                    >{f}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-white">
                {filteredLoads.map(load => (
                  <OrderRow key={load.id} load={load}
                    selected={selectedLoad?.id === load.id}
                    onClick={() => handleSelectLoad(load)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Map full view ── */}
          {mobileView === "map" && (
            <div className="flex-1 flex flex-col pb-16">
              <div className="bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <BackButton to="dashboard"/>
                  <span className="font-bold text-gray-900">Live Tracking</span>
                  {simRunning && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>}
                </div>
                <MapControls compact/>
              </div>
              <div className="flex-1 relative overflow-hidden">
                <TrackingMap simIdx={simIdx} simProg={simProg} simRunning={simRunning} hlDriver={null}/>
                <SimOverlay compact/>
              </div>
              <MapProgress compact/>
            </div>
          )}

          {/* ── Detail / Chat view ── */}
          {mobileView === "detail" && (
            <div className="flex-1 overflow-hidden flex flex-col pb-16 bg-gray-50">
              <div className="bg-white border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
                <BackButton to="dashboard"/>
                <span className="font-bold text-gray-900">Detail / Chat</span>
              </div>
              <div className="flex-1 overflow-hidden flex flex-col">
                <RightPanel
                  rightTab={rightTab} setRightTab={setRightTab}
                  selectedLoad={selectedLoad} selectedDriver={selectedDriver}
                  detailOpen={detailOpen} setDetailOpen={setDetailOpen}
                  simRunning={simRunning} simSpeed={simSpeed}
                  openGPSRecall={() => setGpsModal(true)}
                />
              </div>
            </div>
          )}

          {/* ── Mobile Bottom Nav ── */}
          <BottomBar
            mobileView={mobileView} setMobileView={setMobileView}
            rightTab={rightTab}     setRightTab={setRightTab}
          />
        </div>
      </div>

      {/* ── MODALS ── */}
      {gpsModal && (
        <GPSModal
          simIdx={simIdx} simProg={simProg} simRunning={simRunning}
          onClose={() => setGpsModal(false)}
          onViewMap={d => { setGpsModal(false); setMapPopupDrv(d); }}
        />
      )}
      {mapPopupDrv && (
        <MapPopupModal
          driver={mapPopupDrv} simIdx={simIdx} simProg={simProg} simRunning={simRunning}
          onClose={() => setMapPopupDrv(null)}
        />
      )}
    </div>
  );
}
